import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CircularProgress, FlexSpan, IconButton, Image, Textarea, ThemeName } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import Cookies from "universal-cookie/es6";

import {
  ChatBody,
  ChatContainer,
  ChatFooter,
  ChatHeader,
  ChatHelloMessage,
  ChatMessagesList,
  ChatTextarea,
  ChatTitle,
  ChatTitleGroup,
  LoaderContainer,
  SubmitButton,
} from "./styled";

import { WidthDrag } from "./WidthDrag";
import { Message } from "./Message";

import { useChatActive } from "../../hooks/chat/useChatActive";
import { useChatWidth } from "../../hooks/chat/useChatWidth";
import { getAppConfig } from "../../ducks/appConfig";
import { getThemeName } from "../../ducks/theme/selectors";

import { api, ChatApi } from "../../evergis/api";

import { ChatMessage } from "./types";

import senseLogo from "../../assets/sense_logo.gif";
import senseLogoDark from "../../assets/sense_logo_dark.gif";

const CHAT_AUTOSCROLL_TIMEOUT = 60;

// eslint-disable-next-line max-lines-per-function
export const Chat: FC = () => {
  const { t } = useTranslation("chat");
  const appConfig = useSelector(getAppConfig);
  const themeName = useSelector(getThemeName);
  const [message, setMessage] = useState<string>("");
  const [chatActive, setChatActive] = useChatActive();
  const [chatWidth] = useChatWidth();
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const endOfMessages = useRef<HTMLDivElement | null>(null);

  const onSendMessage = useCallback(async () => {
    if (!message.trim()) {
      return;
    }

    const currentMessage = message;
    let currentChatMessages = [...chatMessages];

    setIsWaiting(true);
    setMessage("");

    textAreaRef.current?.focus();

    currentChatMessages = [
      ...currentChatMessages,
      {
        text: currentMessage,
        isSelf: true,
      },
    ];

    setChatMessages(currentChatMessages);

    setTimeout(() => {
      endOfMessages.current?.scrollIntoView({ behavior: "auto" });
    }, CHAT_AUTOSCROLL_TIMEOUT);

    let jwtToken = new Cookies().get("jwt");

    if (!jwtToken) {
      const updatedLoginInfo = await api.account.refreshToken({
        refreshToken: new Cookies().get("refreshToken"),
      });

      if (updatedLoginInfo) {
        jwtToken = updatedLoginInfo.token;
      }
    }

    new ChatApi({ url: appConfig?.settings?.sense?.url }).chat
      .createMessage({
        message: currentMessage,
        bearerToken: jwtToken,
        session_id: sessionId,
        api_host:
          process.env.NODE_ENV === "development"
            ? `${process.env.SP_URL}${process.env.URL}`
            : `${window.location.protocol}//${window.location.host}${process.env.URL}`,
      })
      .then(response => {
        if (sessionId !== response.session_id) {
          setSessionId(response.session_id);
        }

        currentChatMessages = [
          ...currentChatMessages,
          {
            text: response.response || response.error,
            chartData: response.chartData,
            isSelf: false,
          },
        ];

        setChatMessages(currentChatMessages);
      })
      .catch(() => {
        // ...
      })
      .finally(() => {
        setIsWaiting(false);

        setTimeout(() => {
          endOfMessages.current?.scrollIntoView({ behavior: "auto" });
        }, CHAT_AUTOSCROLL_TIMEOUT);
      });
  }, [chatMessages, message, sessionId]);

  useEffect(() => {
    textAreaRef.current?.focus();
    endOfMessages.current?.scrollIntoView({ behavior: "auto" });
  }, [chatActive]);

  if (!chatActive) {
    return null;
  }

  return (
    <ChatContainer width={chatWidth}>
      <WidthDrag />
      <ChatHeader>
        <ChatTitleGroup>
          <FlexSpan>
            <Image src={themeName === ThemeName.Dark ? senseLogoDark : senseLogo} width={26} height={26} />
          </FlexSpan>
          <FlexSpan>
            <ChatTitle>Sense</ChatTitle>
          </FlexSpan>
        </ChatTitleGroup>
        <FlexSpan>
          <IconButton kind="close" onClick={() => setChatActive(false)} />
        </FlexSpan>
      </ChatHeader>
      <ChatBody>
        {chatMessages.length === 0 && <ChatHelloMessage>Чем я могу вам помочь?</ChatHelloMessage>}
        {chatMessages.length > 0 && (
          <ChatMessagesList>
            {chatMessages.map((messageProps, messageIndex) => (
              <Message key={`chat-list-message-${messageIndex}`} {...messageProps} />
            ))}
            {isWaiting && (
              <LoaderContainer>
                <CircularProgress diameter={1.5} />
              </LoaderContainer>
            )}
          </ChatMessagesList>
        )}
        <div ref={endOfMessages} />
      </ChatBody>
      <ChatFooter isFirstMessage={chatMessages.length === 0}>
        <ChatTextarea>
          <Textarea
            innerRef={textAreaRef}
            height="3.5rem"
            placeholder={t("askSomething")}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={e => {
              if (!e.shiftKey && e.code === "Enter") {
                e.preventDefault();
                onSendMessage();
              }
            }}
          />
          <SubmitButton kind="arrow_back" onClick={onSendMessage} />
        </ChatTextarea>
      </ChatFooter>
    </ChatContainer>
  );
};
